<template>
  <div class="h-full px-4 py-2 text-left relative">
    
    <!-- HEADER -->
    <div class="flex justify-between items-center mb-8 border-b pb-4">
      <div class="w-full text-left mb-4">
        <h4 class="font-bold text-xl text-gray-800">Configuration</h4>
        <p class="text-sm text-gray-500 mt-1">Manage your organization's lists and categories.</p>
      </div>

      <div class="min-w-max flex justify-end items-center h-10 px-4">
        <span v-if="isSaving" class="text-sm text-gray-400 font-semibold flex items-center animate-pulse">Saving...</span>
        <span v-else-if="showSaved" class="text-sm text-green-500 font-semibold flex items-center transition-opacity duration-300">
          <vs-icon icon-pack="feather" icon="icon-check" class="mr-1"></vs-icon> Saved
        </span>
      </div>
    </div>

    <!-- INITIAL LOADING -->
    <div v-if="isLoading" class="flex justify-center py-20">
      <vs-progress indeterminate color="primary" class="w-1/2"></vs-progress>
    </div>

    <!-- CONTENT -->
    <div v-else-if="localConfig">
      
      <div v-for="(key, index) in displayKeys" :key="key" class="mb-10 w-full">
        
        <!-- SECTION HEADER WITH SLEEK BUTTON -->
        <div class="flex items-center mb-4 w-full lg:w-2/3">
          <h5 class="font-bold text-gray-800 text-lg capitalize">
            {{ localConfig[key].title }}
          </h5>
           <div v-if="getTooltipText(key)" class="info-tooltip-wrapper">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="info-icon">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
            <div class="info-tooltip-box">
              {{ getTooltipText(key) }}
            </div>
          </div>
          
          <!-- SLEEK CONFIG BUTTON (Exact Match to Image 1) -->
          <!-- <button
            v-if="key === 'level'"
            @click="openSalaryBandModal"
            title="Configure Salary Bands"
            class="ml-3 flex items-center justify-center bg-white hover:bg-gray-50 transition-colors focus:outline-none"
            style="width: 28px; height: 22px; border: 1px solid #9ca3af; border-radius: 5px; box-shadow: 0 1px 2px rgba(0,0,0,0.05);"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#374151" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="4" y1="6" x2="20" y2="6"></line>
              <line x1="4" y1="12" x2="20" y2="12"></line>
              <line x1="4" y1="18" x2="20" y2="18"></line>
              <circle cx="8" cy="6" r="2.5" fill="#ffffff" stroke="#374151" stroke-width="2"></circle>
              <circle cx="16" cy="12" r="2.5" fill="#ffffff" stroke="#374151" stroke-width="2"></circle>
              <circle cx="10" cy="18" r="2.5" fill="#ffffff" stroke="#374151" stroke-width="2"></circle>
            </svg>
          </button> -->
        </div>

        <!-- TYPE: DROPDOWN -->
        <div v-if="localConfig[key].type === 'dropdown'">
          <div class="flex flex-wrap gap-3 mb-4 justify-start">
            <vs-chip v-for="(item, i) in localConfig[key].values" :key="i" v-if="item.label !== 'Add Configuration'" closable class="custom-chip" @click="removeTag(key, i)">
              <span class="chip-text">{{ item.label }}</span>
            </vs-chip>
            <span v-if="localConfig[key].values.length === 0" class="text-sm text-gray-400 italic py-1">No {{ localConfig[key].title.toLowerCase() }} yet.</span>
          </div>

          <div class="flex items-center w-full md:w-3/4 lg:w-2/3 mt-2">
            <div class="flex-grow relative border border-gray-300 rounded-lg bg-white overflow-hidden transition-all focus-within:shadow-md">
              <vs-input class="w-full input-clean" :placeholder="`+ Add new ${localConfig[key].title}...`" v-model="inputs[key]" @keyup.enter="addTag(key)"/>
            </div>
            <div class="add-btn ml-3 shadow-md hover:shadow-lg transition transform hover:-translate-y-0.5" @click="addTag(key)">
              <vs-icon icon="add" size="24px" color="white"></vs-icon>
            </div>
          </div>
        </div>

        <!-- TYPE: TEXTBOX -->
        <div v-else-if="localConfig[key].type === 'textbox'">
          <div class="w-full md:w-1/2 relative border border-gray-300 rounded-lg bg-white overflow-hidden transition-all focus-within:shadow-md">
            <vs-input class="w-full input-clean" placeholder="Enter value..." v-model="localConfig[key].value" @input="autoSave"/>
          </div>
        </div>

        <!-- FALLBACK -->
        <div v-else class="p-4 bg-gray-50 rounded text-gray-500 text-sm border border-gray-100 lg:w-2/3">
          <span class="font-semibold">Configuration Type:</span> {{ localConfig[key].type }}
        </div>

      </div>
    </div>

    <div v-if="showSalaryModal" class="sb-overlay" @click.self="closeSalaryBandModal">
      <div class="sb-modal">
        
        <!-- Modal Header -->
        <div class="sb-header">
          <div>
            <h2 class="sb-title">Salary Band Configuration</h2>
            <p class="sb-subtitle">Define salary ranges for each role within your organization's configured levels.</p>
          </div>
          <button @click="closeSalaryBandModal" class="sb-close-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>

        <!-- Info Banner -->
        <div class="sb-banner-wrap">
          <div class="sb-banner">
            <span class="sb-banner-text">Salary bands are configured by <b>Role + Level</b>. The same level may have different salary ranges across different roles.</span>
          </div>
        </div>

        <!-- Modal Body -->
        <div class="sb-body custom-scroll">
          
          <!-- Level Selector -->
          <div class="sb-level-section">
            <label class="sb-section-label">SELECT LEVEL</label>
            <div class="sb-tabs">
              <button 
                v-for="lvl in activeLevels" 
                :key="lvl"
                @click="selectedLevelTab = lvl"
                :class="['sb-tab', selectedLevelTab === lvl ? 'sb-tab-active' : '']"
              >
                {{ lvl }}
              </button>
            </div>
          </div>

          <!-- Active Level Content -->
          <div v-if="selectedLevelTab" class="sb-roles-section">
            
            <div class="sb-roles-header">
              <div>
                <h3 class="sb-level-title">{{ selectedLevelTab }} Level</h3>
                <p class="sb-level-desc">Configure compensation ranges for roles at this level.</p>
              </div>
              <span class="sb-role-count">{{ configuredRolesCount }} of {{ rolesInActiveLevel.length }} roles configured</span>
            </div>

            <div class="sb-role-list">
              <div v-for="role in rolesInActiveLevel" :key="role.role_id" class="sb-role-card">
                
                <!-- Top Row: Info and Inputs -->
                <div class="sb-card-top">
                  
                  <!-- Left: Title & Badge -->
                  <div class="sb-role-info">
                    <span class="sb-role-name">{{ role.title }}</span>
                    <span :class="['sb-badge', (role.min_salary || role.max_salary) ? 'sb-badge-configured' : 'sb-badge-unconfigured']">
                      {{ (role.min_salary || role.max_salary) ? 'Configured' : 'Not configured' }}
                    </span>
                  </div>

                  <!-- Right: Inputs -->
                  <div class="sb-role-inputs">
                    <div class="sb-input-group">
                      <label class="sb-input-label">Minimum</label>
                      <div class="sb-input-inner">
                        <span class="sb-currency">€</span>
                        <!-- Added 'hide-arrows' class -->
                        <input type="number" v-model.number="role.min_salary" class="sb-input hide-arrows" placeholder="—" />
                      </div>
                    </div>
                    
                    <div class="sb-input-group">
                      <label class="sb-input-label">Maximum</label>
                      <div class="sb-input-inner">
                        <span class="sb-currency">€</span>
                        <!-- Added 'hide-arrows' class -->
                        <input type="number" v-model.number="role.max_salary" class="sb-input hide-arrows" placeholder="—" />
                      </div>
                    </div>

                    <!-- Clear Band -->
                    <button @click="role.min_salary = null; role.max_salary = null" title="Clear Band" class="sb-clear-btn">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                  </div>
                </div>

                <!-- Bottom Row: Fixed Visual Range Slider -->
                <div class="sb-card-bottom" v-if="role.min_salary || role.max_salary">
                  <!-- Left Label (Min) -->
                  <span class="sb-range-text text-left">
                    {{ formatK(role.min_salary) }}
                  </span>
                  
                  <!-- The Bar (Always 100% full, representing the range) -->
                  <div class="sb-range-track"></div>
                  
                  <!-- Right Label (Max) -->
                  <span class="sb-range-text text-right">
                    {{ formatK(role.max_salary) }}
                  </span>
                </div>
              </div>
              
              <!-- Empty State -->
              <div v-if="rolesInActiveLevel.length === 0" class="sb-empty-state">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="sb-empty-icon"><polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 17 12 22 22 17"></polyline><polyline points="2 12 12 17 22 12"></polyline></svg>
                <p class="sb-empty-title">No roles are currently assigned to the {{ selectedLevelTab }} level.</p>
                <span class="sb-empty-desc">Go to the Org Chart and update your roles' levels first.</span>
              </div>

            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="sb-footer">
          <button @click="closeSalaryBandModal" class="sb-btn sb-btn-cancel">Cancel</button>
          <button @click="saveSalaryBands()" :disabled="isSavingBands" class="sb-btn sb-btn-save">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right:6px;"><line x1="4" y1="21" x2="4" y2="14"></line><line x1="4" y1="10" x2="4" y2="3"></line><line x1="12" y1="21" x2="12" y2="12"></line><line x1="12" y1="8" x2="12" y2="3"></line><line x1="20" y1="21" x2="20" y2="16"></line><line x1="20" y1="12" x2="20" y2="3"></line><line x1="1" y1="14" x2="7" y2="14"></line><line x1="9" y1="8" x2="15" y2="8"></line><line x1="17" y1="16" x2="23" y2="16"></line></svg>
            {{ isSavingBands ? 'Saving...' : 'Save Salary Bands' }}
          </button>
        </div>
        
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'OrgSettingsConfig',
  data() {
    return {
      isLoading: false,
      isSaving: false,
      showSaved: false,
      saveTimeout: null,
      localConfig: null,
      displayKeys: [
        'department',
        'roleTitle',
        'level',
        'numberOfDirectReports',
        'performance',
        'potential',
        'relationship'
      ],
      inputs: {
        department: '',
        roleTitle: '',
        level: '',
        numberOfDirectReports: '',
        performance: '',
        potential: '',
        relationship: ''
      },

      // --- ADD THESE 4 EXACTLY AS WRITTEN FOR THE MODAL ---
      showSalaryModal: false,
      isSavingBands: false,
      selectedLevelTab: '',
      allRoleMetadata: []
    };
  },
  computed: {
    ...mapState('organization', ['organizationConfiguration', 'orgData']),

    activeLevels() {
      if (!this.localConfig || !this.localConfig.level || !this.localConfig.level.values) return [];
      return this.localConfig.level.values.map(v => v.label);
    },
    rolesInActiveLevel() {
      if (!this.selectedLevelTab) return [];
      return this.allRoleMetadata.filter(role => role.level === this.selectedLevelTab);
    },
    configuredRolesCount() {
      return this.rolesInActiveLevel.filter(r => r.min_salary || r.max_salary).length;
    }
  },
  watch: {
    organizationConfiguration: {
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          // IMPORTANT: Only populate localConfig if it hasn't been set yet.
          if (!this.localConfig) {
            this.localConfig = JSON.parse(JSON.stringify(newVal));

            if(this.localConfig.department) {
              this.localConfig.department.title = "Groups";
            }

            if(this.localConfig.roleTitle) {
              this.localConfig.roleTitle.title = "Role Type";
            }

            // --- SMART DEFAULTS INJECTION ---
            // We check for a hidden config key 'frontendSetupComplete'
            if (!this.localConfig.frontendSetupComplete) {
              let hasChanges = false;

              const injectDefaults = (key, defaultLabels) => {
                // Ensure the object and array structures exist
                if (!this.localConfig[key]) {
                  this.$set(this.localConfig, key, { type: 'dropdown', title: key, values: [] });
                }
                if (!this.localConfig[key].values) {
                  this.$set(this.localConfig[key], 'values', []);
                }
                
                // IMPORTANT FIX: If there are already items, or the setup ran before, 
                // DO NOT do a deep merge. This respects the user's deletions!
                if (this.localConfig[key].values.length > 0) {
                  return;
                }
                
                // Map the default labels into the correct object structure
                const newItems = defaultLabels.map((label, idx) => ({
                  key: label.replace(/\s+/g, '').toLowerCase() + '_def_' + Date.now() + idx,
                  label: label
                }));

                this.localConfig[key].values = newItems;
                hasChanges = true;
              };

              // 1. Inject Roles
              injectDefaults('roleTitle', [
                'Intern', 'Trainee', 'Coach', 'Manager', 'Consultant', 
                'Contributor', 'Team Lead', 'Applicant Lead', 'Business Lead'
              ]);

              // 2. Inject Levels
              injectDefaults('level', [
                'Intern', 'Trainee', 'Individual Performer', 'Lead', 
                'Manager', 'Director', 'Executive'
              ]);

              // 3. Inject Relationships
              injectDefaults('relationship', [
                'Coaching', 'Trainee', 'Advisor', 'Mentoring', 
                'Leading', 'Likely Collaboratoring', 'Prospect', 'Applicant'
              ]);

              this.$set(this.localConfig, 'frontendSetupComplete', {
                type: 'system_flag',
                value: true
              });
              
              hasChanges = true; // Force save so the database remembers setup is complete

              // Trigger auto-save to database
              if (hasChanges) {
                this.autoSave();
              }
            }
          }
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
  getTooltipText(key) {
      const tooltips = {
        department: 'Organizational groups used to categorize roles and structure role design within the organization.',

        roleTitle: 'Defines the role type and associated access level, which will determines the criteria used for role-based evaluation.',

        level: 'Specifies the hierarchy level of a role within the organization. This is used to define role progression and structure in the role design.',

        relationship: 'Defines organizational relationships between individuals, such as an Intern reporting to a Manager. These relationships are reflected in the organizational network graph in Reports and can be managed through the Talent Pool.'
      };
      // Returns the text if it exists in the dictionary, otherwise null (hides the icon)
      return tooltips[key] || null;
    },

    addTag(key) {
      const val = this.inputs[key];
      if (!val || val.trim() === '') return;

      if (!this.localConfig[key].values) {
        this.$set(this.localConfig[key], 'values', []);
      }

      const newKey = val.trim().replace(/\s+/g, '').toLowerCase() + '_' + Date.now();

      this.localConfig[key].values.push({
        key: newKey,
        label: val.trim()
      });

      this.inputs[key] = '';
      this.autoSave(); // Trigger auto-save
    },

    removeTag(key, index) {
      this.localConfig[key].values.splice(index, 1);
      this.autoSave(); // Trigger auto-save
    },

    autoSave() {
      // Show saving status in UI
      this.isSaving = true;
      this.showSaved = false;
      
      // Clear any pending save requests (Debouncing)
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
      }

      // Wait 1 second after the user stops typing/clicking before saving
      this.saveTimeout = setTimeout(() => {
        this.saveChanges();
      }, 1000);
    },

    saveChanges() {
      if (!this.orgData || !this.orgData._id) return;

      const payload = {
        organizationId: this.orgData._id,
        configuration: this.localConfig
      };

      this.$store.dispatch('organization/updateOrganizationConfig', payload)
        .then(() => {
          // Show "Saved" temporarily
          this.showSaved = true;
          setTimeout(() => {
            this.showSaved = false;
          }, 3000); // Hide the checkmark after 3 seconds
        })
        .catch(err => {
          console.error(err);
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to auto-save configuration',
            color: 'danger',
            icon: 'alert-circle'
          });
        })
        .finally(() => {
          this.isSaving = false;
        });
    },

    fetchConfig() {
      if (this.orgData && this.orgData._id) {
        // Initial load indication only
        this.isLoading = true;
        this.$store.dispatch('organization/fetchOrganizationConfig', {
          organizationId: this.orgData._id
        }).finally(() => {
          this.isLoading = false;
        });
      }
    },

    closeSalaryBandModal() {
      this.showSalaryModal = false;
      this.selectedLevelTab = ''; // Reset tab on close
    },

    async openSalaryBandModal() {
      this.showSalaryModal = true;
      if (this.activeLevels.length > 0) {
        this.selectedLevelTab = this.activeLevels[0]; // Auto-select first tab
      }
      
      try {
        const pyurl = process.env.VUE_APP_API_URL;
        const token = localStorage.getItem('accessToken') || localStorage.getItem('feathers-jwt');
        const response = await fetch(`${pyurl}/api/v2/organization/${this.orgData._id}/salary-bands`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const json = await response.json();
          this.allRoleMetadata = json.data || [];
        }
      } catch (err) {
        console.error("Failed to load salary bands:", err);
      }
    },

    async saveSalaryBands() {
      this.isSavingBands = true;
      try {
        const pyurl = process.env.VUE_APP_API_URL;
        const token = localStorage.getItem('accessToken') || localStorage.getItem('feathers-jwt');
        const payload = { bands: this.allRoleMetadata };

        const response = await fetch(`${pyurl}/api/v2/organization/${this.orgData._id}/salary-bands`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify(payload)
        });
        
        if (response.ok) {
          this.$vs.notify({ title: 'Success', text: 'Salary bands saved successfully.', color: 'success' });
          this.closeSalaryBandModal();
        } else {
          throw new Error("API rejected the save request");
        }
      } catch (err) {
        console.error("Save error:", err);
        this.$vs.notify({ title: 'Error', text: 'Could not save salary bands.', color: 'danger' });
      } finally {
        this.isSavingBands = false;
      }
    },

    formatK(value) {
      if (!value && value !== 0) return '';
      // Better formatting for small test numbers
      if (value < 1000) return '€' + value; 
      
      // If it's a clean thousand (26000), show €26k. If decimal (26500), show €26.5k
      return '€' + (value / 1000).toFixed(value % 1000 !== 0 ? 1 : 0) + 'k';
    },

    getRangeStyle(min, max) {
      if (!min && !max) return { display: 'none' };
      
      const safeMin = min || 0;
      const safeMax = Math.max(max || 0, min || 0); // Prevents visual break if max is accidentally lower than min
      
      // DYNAMIC SCALING LOGIC:
      // Instead of a hardcoded 150k, we set the gray track's "end limit" to 125% of the max value.
      // This gives the yellow bar a beautiful, prominent size with 25% breathing room on the right.
      const trackEndLimit = safeMax > 0 ? safeMax * 1.25 : (safeMin > 0 ? safeMin * 2 : 100);
      
      let leftPercent = (safeMin / trackEndLimit) * 100;
      let widthPercent = ((safeMax - safeMin) / trackEndLimit) * 100;
      
      // UI Protections to keep it looking perfect
      if (leftPercent > 90) leftPercent = 90; // Don't push completely off the right edge
      if (widthPercent < 2) widthPercent = 2; // Show at least a tiny sliver if Min == Max
      if (leftPercent + widthPercent > 100) widthPercent = 100 - leftPercent; // Prevent overflow

      return { left: `${leftPercent}%`, width: `${widthPercent}%` };
    }
  },
  mounted() {
    this.fetchConfig();
  }
};
</script>

<style scoped>
/* CHIP & INPUT STYLING (Existing) */
.custom-chip { background: white !important; border: 1px solid rgba(var(--vs-primary), 0.3) !important; height: 36px; border-radius: 12px !important; transition: all 0.2s ease; display: flex; align-items: center; padding: 0 10px; }
.custom-chip:hover { background: rgba(var(--vs-primary), 0.25) !important; }
.chip-text { color: rgba(var(--vs-primary), 1); font-weight: 700; font-size: 0.95rem; padding-right: 5px; }
::v-deep .custom-chip .vs-chip--close { background-color: rgba(var(--vs-primary), 1) !important; color: white !important; opacity: 1 !important; border-radius: 50%; width: 20px; height: 20px; display: flex !important; align-items: center; justify-content: center; margin-left: 8px; transform: scale(0.9); transition: all 0.2s ease-in-out; }
::v-deep .custom-chip .vs-chip--close .vs-icon { color: inherit !important; font-size: 14px !important; }
::v-deep .custom-chip .vs-chip--close:hover { background-color: white !important; color: white !important; transform: scale(1.1); box-shadow: 0 2px 5px rgba(0,0,0,0.2); }
::v-deep .input-clean .vs-input--input { border: none !important; background: transparent !important; padding: 12px 16px !important; box-shadow: none !important; font-size: 1rem; color: #4a5568; text-align: left !important; }
::v-deep .input-clean .vs-input--placeholder { text-align: left !important; color: #a0aec0; }
.add-btn { width: 46px; height: 46px; min-width: 46px; border-radius: 50%; background: rgba(var(--vs-primary), 1); display: flex; align-items: center; justify-content: center; cursor: pointer; }
.add-btn:hover { background: rgba(var(--vs-primary), 0.9); }
.hide-arrows::-webkit-outer-spin-button,
.hide-arrows::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.hide-arrows {
  -moz-appearance: textfield;
}
/* ==============================================================
   NEW SALARY BAND MODAL STYLES (LOVABLE DESIGN)
   ============================================================== */

.sb-overlay {
  position: fixed;
  top: 0; left: 0; width: 100vw; height: 100vh;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center;
  z-index: 99999; padding: 20px; box-sizing: border-box;
}

.sb-modal {
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  width: 100%; max-width: 800px; max-height: 90vh;
  display: flex; flex-direction: column;
  overflow: hidden; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

/* Header */
.sb-header { padding: 20px 24px; display: flex; justify-content: space-between; align-items: flex-start; }
.sb-title { margin: 0; font-size: 18px; font-weight: 700; color: #111827; }
.sb-subtitle { margin: 4px 0 0 0; font-size: 13px; color: #64748b; }
.sb-close-btn { background: transparent; border: none; cursor: pointer; color: #94a3b8; padding: 4px; border-radius: 50%; display: flex; align-items: center; justify-content: center; transition: background 0.2s; }
.sb-close-btn:hover { background-color: #f1f5f9; color: #1e293b; }

/* Banner */
.sb-banner-wrap { padding: 0 24px 16px 24px; }
.sb-banner {
  background-color: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 12px 16px;
}
.sb-banner-text { font-size: 13px; color: #92400e; display: block; }
.sb-banner-text b { color: #78350f; font-weight: 700; }

/* Body & Scroll */
.sb-body { padding: 0 24px 24px 24px; flex: 1; overflow-y: auto; }
.custom-scroll::-webkit-scrollbar { width: 6px; }
.custom-scroll::-webkit-scrollbar-track { background: transparent; }
.custom-scroll::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 10px; }
.custom-scroll::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; }

/* Levels Selector */
.sb-level-section { margin-bottom: 24px; }
.sb-section-label { display: block; font-size: 10px; font-weight: 700; color: #94a3b8; letter-spacing: 0.05em; margin-bottom: 8px; }
.sb-tabs { display: flex; flex-wrap: wrap; gap: 8px; }
.sb-tab {
  background: #ffffff; border: 1px solid #cbd5e1; color: #475569;
  padding: 6px 16px; border-radius: 50px; font-size: 13px;
  cursor: pointer; transition: all 0.2s; font-weight: 500;
}
.sb-tab:hover { border-color: #94a3b8; color: #1e293b; }
.sb-tab-active { background: #F2C244 !important; border-color: #F2C244 !important; color: #111827 !important; font-weight: 600; }

/* Roles Section Header */
.sb-roles-header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 1px solid #f1f5f9; padding-bottom: 8px; margin-bottom: 16px; }
.sb-level-title { margin: 0; font-size: 16px; font-weight: 700; color: #1e293b; }
.sb-level-desc { margin: 2px 0 0 0; font-size: 12px; color: #64748b; }
.sb-role-count { font-size: 11px; font-weight: 600; color: #94a3b8; }

/* Role Card (Matches Lovable) */
.sb-role-list { display: flex; flex-direction: column; gap: 16px; }
.sb-role-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  display: flex; flex-direction: column; gap: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.02);
}

/* Card Top: Inputs */
.sb-card-top { display: flex; align-items: center; justify-content: space-between; }
.sb-role-info { display: flex; align-items: center; gap: 12px; }
.sb-role-name { font-size: 14px; font-weight: 700; color: #0f172a; }

/* Badges (Styled to match Lovable but keeping GetBoarded colors) */
.sb-badge { font-size: 11px; font-weight: 600; padding: 4px 10px; border-radius: 16px; border: 1px solid transparent; }
.sb-badge-configured { background: #fef9c3; color: #854d0e; border-color: #fde047; }
.sb-badge-unconfigured { background: #f8fafc; color: #64748b; border-color: #cbd5e1; }

/* Inputs (Pill shape and label alignment) */
.sb-role-inputs { display: flex; align-items: flex-end; gap: 12px; }
.sb-input-group { display: flex; flex-direction: column; align-items: center; }
.sb-input-label { font-size: 11px; font-weight: 500; color: #64748b; margin-bottom: 6px; }
.sb-input-inner { position: relative; display: flex; align-items: center; }
.sb-currency { position: absolute; left: 14px; color: #64748b; font-size: 13px; font-weight: 600; }
.sb-input {
  width: 125px; padding: 8px 12px 8px 28px; border: 1px solid #cbd5e1; border-radius: 50px;
  font-size: 13px; font-weight: 600; color: #1e293b; outline: none; box-sizing: border-box; transition: border 0.2s;
}
.sb-input:focus { border-color: #F2C244; box-shadow: 0 0 0 2px rgba(242, 194, 68, 0.2); }

.sb-clear-btn {
  background: transparent; border: 1px solid #cbd5e1; color: #94a3b8; cursor: pointer;
  width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; transition: all 0.2s;
  margin-bottom: 5px; /* Visual alignment with pill inputs */
}
.sb-clear-btn:hover { color: #64748b; border-color: #94a3b8; background: #f8fafc; }

/* Card Bottom: Range Slider Visuals */
/* --- FIXED RANGE BAR STYLES (Lovable Inline Style) --- */
.sb-card-bottom { 
  display: flex; 
  align-items: center; 
  gap: 12px; 
  margin-top: 12px; 
  padding: 0 4px;
}

.sb-range-text { 
  font-size: 11px; 
  font-weight: 600; 
  color: #64748b; 
  white-space: nowrap; 
  min-width: 32px; /* Keeps the bar from jumping around if text changes */
}

.sb-range-text.text-left { text-align: left; }
.sb-range-text.text-right { text-align: right; }

.sb-range-track { 
  flex-grow: 1; /* Stretches to fill all available space between labels */
  height: 6px; 
  background-color: #F2C244; /* The whole bar is yellow */
  border: 1px solid #1e293b; /* Dark outline exactly like the mockup */
  border-radius: 6px; 
  position: relative; 
}
.sb-range-labels-container { position: relative; height: 16px; margin-top: 6px; width: 100%; }
.sb-range-label { position: absolute; top: 12px; font-size: 11px; font-weight: 600; color: #64748b; white-space: nowrap; }
.sb-label-left { left: 0; transform: translateX(-30%); }
.sb-label-right { right: 0; transform: translateX(30%); }
/* Empty State & Fake Add Button */
.sb-empty-state { text-align: center; padding: 40px 20px; background: #ffffff; border: 1px dashed #cbd5e1; border-radius: 12px; }
.sb-empty-icon { margin-bottom: 8px; }
.sb-empty-title { margin: 0 0 4px 0; font-size: 14px; font-weight: 600; color: #475569; }
.sb-empty-desc { font-size: 12px; color: #94a3b8; }

.sb-fake-add-btn {
  background: #ffffff; border: 1px dashed #cbd5e1; color: #64748b; font-size: 13px; font-weight: 500;
  padding: 12px; border-radius: 12px; display: flex; align-items: center; justify-content: center; gap: 8px; cursor: pointer; transition: background 0.2s;
}
.sb-fake-add-btn:hover { background: #f8fafc; }

/* Footer */
.sb-footer {
  padding: 16px 24px; background: #ffffff; border-top: 1px solid #f1f5f9;
  display: flex; justify-content: flex-end; gap: 12px;
}
.sb-btn {
  padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600;
  cursor: pointer; display: flex; align-items: center; transition: background 0.2s;
}
.sb-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.sb-btn-cancel { background: #ffffff; border: 1px solid #cbd5e1; color: #475569; }
.sb-btn-cancel:hover { background: #f8fafc; }
.sb-btn-save { background: #F2C244; border: 1px solid transparent; color: #111827; }
.sb-btn-save:hover:not(:disabled) { background: #eab308; }
/* ==============================================================
   INFO TOOLTIP STYLES
   ============================================================== */
.info-tooltip-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 10px;
  cursor: help;
}

.info-icon {
  transition: stroke 0.2s ease;
}

.info-tooltip-wrapper:hover .info-icon {
  stroke: #4b5563; 
}

.info-tooltip-box {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: 130%; 
  /* CHANGE: Anchor to the left side instead of center, extending right */
  left: -12px; 
  transform: none; /* Removed the -50% translation */
  width: max-content; /* Let it size to the text */
  max-width: 280px; /* But don't let it get too wide */
  background-color: #1f2937;
  color: #ffffff;
  text-align: left; /* Better readability for longer text aligned this way */
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;
  font-weight: 500;
  z-index: 99999;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  transition: opacity 0.2s ease, visibility 0.2s ease;
  pointer-events: none; 
}

/* Tooltip Arrow pointing down */
.info-tooltip-box::after {
  content: "";
  position: absolute;
  top: 100%;
  /* CHANGE: Move arrow to align exactly under the 'i' icon */
  left: 20px; 
  margin-left: -6px;
  border-width: 6px;
  border-style: solid;
  border-color: #1f2937 transparent transparent transparent;
}

/* Trigger hover state */
.info-tooltip-wrapper:hover .info-tooltip-box {
  visibility: visible;
  opacity: 1;
}
</style>